/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import dayjs from 'dayjs';
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
} from '@mui/material';
import { Calendar } from 'primereact/calendar';

import UseAxios from '../../../Api/http/http';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import dropdwn from '../../../Assets/images/svg/dar_dropdown_arrow.svg';
import { zohoClientDetailsList } from '../../../Api/request/zohoClientDetails';
import {
  getMilestoneDropdownOptions,
  searchPoNumbers,
  createMilestone,
  updateMilestone,
  getSingleMilestone,
} from '../../../Api/request/milestone';

/* -------------------------------------------------------------------------- */
/*  Date helpers                                                               */
/* -------------------------------------------------------------------------- */
const MONTH_ABBR = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

const parseDateForInput = (dateVal) => {
  if (!dateVal || dateVal === '-' || dateVal === '9999-01-01' || dateVal === '0000-00-00') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) return dateVal;
  const match = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(dateVal);
  if (match) return `${match[3]}-${MONTH_ABBR[match[2]] || '01'}-${match[1].padStart(2, '0')}`;
  return '';
};

const mapZohoClientOptions = (response) => {
  const payload = response?.data?.data;
  const list = Array.isArray(payload)
    ? payload
    : (payload?.tableData
      || payload?.zohoCustomerDetails
      || payload?.zohoCustomers
      || payload?.customers
      || payload?.data
      || payload?.result
      || []);

  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      const id = item?.zohoCustomerId ?? item?.zoho_customer_id ?? item?.id;
      const label = item?.clientName ?? item?.zohoCustomerName ?? item?.customerName ?? item?.name ?? item?.label;
      if (id == null || !label) return null;
      return {
        id: String(id),
        name: String(label),
        label: String(label),
        value: String(label),
      };
    })
    .filter(Boolean);
};

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */
const EMPTY_FORM = {
  poId: '',
  poText: '',
  milestoneStatusId: '',
  milestoneTypeId: '',
  invoiceStatusId: '',
  zohoClientId: '',
  milestone: '',
  category: '',
  contValueP: '',
  amount: '',
  cogs: '',
  ga: '',
  tp: '',
  dueDate: '',
  replannedDueDate: '',
  remarks: '',
};

/* -------------------------------------------------------------------------- */
/*  MilestoneFormDialog                                                        */
/* -------------------------------------------------------------------------- */
const MilestoneFormDialog = ({
  visible,
  onHide,
  mode,
  projectId,
  rowData,
  token,
  onSuccess,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filteredPoNumbers, setFilteredPoNumbers] = useState([]);
  const [showPoSuggestions, setShowPoSuggestions] = useState(false);

  const [msStatuses, setMsStatuses] = useState([]);
  const [msTypes, setMsTypes] = useState([]);
  const [invStatuses, setInvStatuses] = useState([]);
  const [zohoClients, setZohoClients] = useState([]);

  const poWrapRef = useRef(null);
  const poSearchTimerRef = useRef(null);

  useEffect(() => {
    if (!visible) return;
    setError('');
    setShowPoSuggestions(false);
    setFilteredPoNumbers([]);

    // rowData from flattened table uses camelCase; support both
    const editMilestoneId = rowData?.milestoneId ?? rowData?.milestone_id;

    const requests = [
      UseAxios(getMilestoneDropdownOptions('milestone-statuses')),
      UseAxios(getMilestoneDropdownOptions('milestone-types')),
      UseAxios(getMilestoneDropdownOptions('invoice-statuses')),
      UseAxios(zohoClientDetailsList()),
    ];

    if (mode === 'edit' && editMilestoneId) {
      requests.push(UseAxios(getSingleMilestone(editMilestoneId)));
    }

    setLoading(true);
    Promise.all(requests).then(([msRes, mtRes, isRes, zcRes, milRes]) => {
      setMsStatuses(msRes?.data?.data || []);
      setMsTypes(mtRes?.data?.data || []);
      setInvStatuses(isRes?.data?.data || []);

      const mappedZohoClients = mapZohoClientOptions(zcRes);
      setZohoClients(mappedZohoClients);

      if (milRes) {
        const data = milRes?.data?.data || {};
        setForm({
          poId: data.po_id ? String(data.po_id) : '',
          poText: data.po_number || '',
          milestoneStatusId: data.milestone_status_id ? String(data.milestone_status_id) : '',
          milestoneTypeId: data.milestone_type_id ? String(data.milestone_type_id) : '',
          invoiceStatusId: data.invoice_status_id ? String(data.invoice_status_id) : '',
          zohoClientId: data.zoho_client_id ? String(data.zoho_client_id) : '',
          milestone: data.milestone || '',
          category: data.category || '',
          contValueP: data.cont_value_p ?? '',
          amount: data.amount ?? '',
          cogs: data.cogs ?? '',
          ga: data.ga ?? '',
          tp: data.tp ?? '',
          dueDate: parseDateForInput(data.due_date),
          replannedDueDate: parseDateForInput(data.replanned_due_date),
          remarks: data.remarks || '',
        });
      } else {
        const pendingStatus = (msRes?.data?.data || []).find(
          (status) => status.label?.toLowerCase() === 'pending to invoice',
        );
        const noCommentsStatus = (isRes?.data?.data || []).find((status) => {
          const lbl = (status.label || status.name || status.value || '').toLowerCase().trim();
          return lbl === 'no comments' || lbl === 'no comment';
        });

        setForm({
          ...EMPTY_FORM,
          milestoneStatusId: pendingStatus ? String(pendingStatus.id) : '',
          invoiceStatusId: noCommentsStatus ? String(noCommentsStatus.id) : '',
        });
      }
      setLoading(false);
    });
  }, [visible, mode, projectId, rowData]);

  if (!visible) return null;

  const setField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const renderSelectValue = (selectedValue, options) => {
    if (!selectedValue) {
      return <span className="ms-dialog__placeholder">Select</span>;
    }
    const match = options.find((item) => String(item.id) === String(selectedValue));
    return match ? match.label : selectedValue;
  };

  const getSelectedZohoClient = (selectedId, options = []) => {
    if (!selectedId) return null;
    const found = options.find((item) => String(item.id) === String(selectedId));
    return found || null;
  };

  const handleSubmit = async () => {
    if (!form.milestone.trim()) {
      setError('Milestone name is required.');
      return;
    }
    if (!form.milestoneTypeId) {
      setError('Milestone type is required.');
      return;
    }

    setSubmitting(true);
    setError('');

    const payload = { projectId, ...form };
    const req = mode === 'edit'
      ? updateMilestone(rowData.milestoneId ?? rowData.milestone_id, payload)
      : createMilestone(payload);

    const result = await UseAxios({ ...req, headers: { ...req.headers, token } });
    setSubmitting(false);

    if (result?.data?.success) {
      onSuccess?.(mode === 'edit' ? 'Milestone updated successfully.' : 'Milestone added successfully.');
      onHide();
    } else {
      setError(result?.data?.message || 'Operation failed. Please try again.');
    }
  };

  return (
    <div className="ms-dialog-overlay" role="dialog" aria-modal="true">
      <div className="ms-dialog ms-dialog--md ms-dialog--milestone-form">
        <div className="ms-dialog__header">
          {mode === 'edit' ? 'Edit Milestone' : 'Add Milestone'}
        </div>

        {loading ? (
          <div className="ms-dialog__loading">Loading...</div>
        ) : (
          <div className="ms-dialog__form ms-dialog__form--2col ms-dialog__form--milestone">
            {mode === 'edit' && (
              <div className="ms-dialog__field">
                <label className="ms-dialog__label">Milestone ID</label>
                <input
                  type="text"
                  className="ms-dialog__input ms-dialog__input--readonly"
                  value={rowData?.milestoneId ?? rowData?.milestone_id ?? ''}
                  readOnly
                />
              </div>
            )}

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">PO Number</label>
              <div style={{ position: 'relative' }} ref={poWrapRef}>
                <input
                  type="text"
                  className="ms-dialog__input"
                  placeholder="PO Number"
                  autoComplete="off"
                  value={form.poText}
                  onChange={(ev) => {
                    const text = ev.target.value;
                    setForm((prev) => ({ ...prev, poText: text, poId: '' }));
                    clearTimeout(poSearchTimerRef.current);

                    if (text.length >= 2) {
                      poSearchTimerRef.current = setTimeout(async () => {
                        const res = await UseAxios(searchPoNumbers(text));
                        const results = res?.data?.data || [];
                        setFilteredPoNumbers(results);

                        const exactMatch = results.find(
                          (po) => String(po.po_number || '').trim()
                            .toLowerCase() === text.trim()
                            .toLowerCase(),
                        );

                        if (exactMatch) {
                          setForm((prev) => ({ ...prev, poId: String(exactMatch.po_id), poText: exactMatch.po_number }));
                          setShowPoSuggestions(false);
                        } else {
                          setShowPoSuggestions(results.length > 0);
                        }
                      }, 300);
                    } else {
                      setShowPoSuggestions(false);
                      setFilteredPoNumbers([]);
                    }
                  }}
                  onBlur={() => {
                    const exactMatch = filteredPoNumbers.find(
                      (po) => String(po.po_number || '').trim()
                        .toLowerCase() === String(form.poText || '').trim()
                        .toLowerCase(),
                    );

                    if (exactMatch) {
                      setForm((prev) => ({ ...prev, poText: exactMatch.po_number, poId: String(exactMatch.po_id) }));
                    }

                    setTimeout(() => setShowPoSuggestions(false), 200);
                  }}
                />
                {showPoSuggestions && (
                  <div className="ms-po-suggestions">
                    {filteredPoNumbers.map((po) => (
                      <div
                        key={po.po_id}
                        className="ms-po-suggestion-item"
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          setForm((prev) => ({
                            ...prev,
                            poText: po.po_number,
                            poId: String(po.po_id),
                          }));
                          setShowPoSuggestions(false);
                          setFilteredPoNumbers([]);
                        }}
                      >
                        {po.po_number}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="ms-dialog-field">
              <label className="ms-dialog-label">
                Milestone Type <span className="ms-dialog-required">*</span>
              </label>
              <div className="ms-dialog-select-wrap">
                <FormControl fullWidth size="small">
                  <MuiSelect
                    displayEmpty
                    value={form.milestoneTypeId}
                    onChange={(ev) => {
                      setField('milestoneTypeId', ev.target.value);
                    }}
                    input={<OutlinedInput className="ms-dialog-input ms-dialog-input--mui" />}
                    renderValue={(selectedValue) => renderSelectValue(selectedValue, msTypes)}
                    IconComponent={() => null}
                    MenuProps={{
                      disablePortal: false,
                      sx: {
                        zIndex: 9105,
                      },
                      MenuListProps: {
                        sx: {
                          maxHeight: 181,
                          overflowY: 'auto',
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      },
                      PaperProps: {
                        className: 'ms-dialog-menu-paper',
                        sx: {
                          overflow: 'hidden',
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <span className="ms-dialog-placeholder">Select</span>
                    </MenuItem>
                    {msTypes.map((mtItem) => (
                      <MenuItem key={mtItem.id} value={String(mtItem.id)}>{mtItem.label}</MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <img src={dropdwn} alt="dropdown" className="ms-dialog-select-icon" />
              </div>
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                Milestone Status <span className="ms-dialog__required">*</span>
              </label>
              <div className="ms-dialog__select-wrap">
                <FormControl fullWidth size="small">
                  <MuiSelect
                    displayEmpty
                    value={form.milestoneStatusId}
                    onChange={(ev) => {
                      setField('milestoneStatusId', ev.target.value);
                    }}
                    input={<OutlinedInput className="ms-dialog__input ms-dialog__input--mui" />}
                    renderValue={(selectedValue) => renderSelectValue(selectedValue, msStatuses)}
                    IconComponent={() => null}
                    MenuProps={{
                      disablePortal: false,
                      sx: {
                        zIndex: 9105,
                      },
                      MenuListProps: {
                        sx: {
                          maxHeight: 181,
                          overflowY: 'auto',
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      },
                      PaperProps: {
                        className: 'ms-dialog__menu-paper',
                        sx: {
                          overflow: 'hidden',
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <span className="ms-dialog__placeholder">Select</span>
                    </MenuItem>
                    {msStatuses.map((msItem) => (
                      <MenuItem key={msItem.id} value={String(msItem.id)}>{msItem.label}</MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <img src={dropdwn} alt="dropdown" className="ms-dialog__select-icon" />
              </div>
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                Invoice Status <span className="ms-dialog__required">*</span>
              </label>
              <div className="ms-dialog__select-wrap">
                <FormControl fullWidth size="small">
                  <MuiSelect
                    displayEmpty
                    value={form.invoiceStatusId}
                    onChange={(ev) => {
                      setField('invoiceStatusId', ev.target.value);
                    }}
                    input={<OutlinedInput className="ms-dialog__input ms-dialog__input--mui" />}
                    renderValue={(selectedValue) => renderSelectValue(selectedValue, invStatuses)}
                    IconComponent={() => null}
                    MenuProps={{
                      disablePortal: false,
                      sx: {
                        zIndex: 9105,
                      },
                      MenuListProps: {
                        sx: {
                          maxHeight: 181,
                          overflowY: 'auto',
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      },
                      PaperProps: {
                        className: 'ms-dialog__menu-paper',
                        sx: {
                          overflow: 'hidden',
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <span className="ms-dialog__placeholder">Select</span>
                    </MenuItem>
                    {invStatuses.map((isItem) => (
                      <MenuItem key={isItem.id} value={String(isItem.id)}>{isItem.label}</MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <img src={dropdwn} alt="dropdown" className="ms-dialog__select-icon" />
              </div>
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                Milestone <span className="ms-dialog__required">*</span>
              </label>
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter Milestone"
                value={form.milestone}
                onChange={(ev) => {
                  setField('milestone', ev.target.value);
                }}
              />
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                Category <span className="ms-dialog__required">*</span>
              </label>
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter..."
                value={form.category}
                onChange={(ev) => {
                  setField('category', ev.target.value);
                }}
              />
            </div>

            <div className="ms-dialog-field">
              <label className="ms-dialog-label">
                Zoho Client Name
              </label>
              <SearchDropdown
                options={zohoClients}
                value={getSelectedZohoClient(form.zohoClientId, zohoClients)}
                placeholder="Select"
                menuZIndex={9105}
                dropdownCss="ms-zoho-dropdown"
                onChange={(event) => {
                  const selected = event?.target?.value;
                  setField('zohoClientId', selected?.id ? String(selected.id) : '');
                }}
              />
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                % (Of Contract Value) <span className="ms-dialog__required">*</span>
              </label>
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter %"
                value={form.contValueP}
                onChange={(ev) => {
                  setField('contValueP', ev.target.value);
                }}
              />
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                Due Amount (INR) <span className="ms-dialog__required">*</span>
              </label>
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Due Amount (INR)"
                value={form.amount}
                onChange={(ev) => {
                  setField('amount', ev.target.value);
                }}
              />
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">COGS (INR)</label>
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter COGS (INR)"
                value={form.cogs}
                onChange={(ev) => {
                  setField('cogs', ev.target.value);
                }}
              />
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">GA (INR)</label>
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter GA (INR)"
                value={form.ga}
                onChange={(ev) => {
                  setField('ga', ev.target.value);
                }}
              />
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">TP (INR)</label>
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter TP (INR)"
                value={form.tp}
                onChange={(ev) => {
                  setField('tp', ev.target.value);
                }}
              />
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                Due Date <span className="ms-dialog__required">*</span>
              </label>
              <div className="ms-dialog__date-picker">
                <Calendar
                  value={form.dueDate ? new Date(form.dueDate) : null}
                  onChange={(val) => {
                    const newValue = val.value;
                    if (!newValue) {
                      setField('dueDate', '');
                      return;
                    }
                    setField('dueDate', dayjs(newValue).format('YYYY-MM-DD'));
                  }}
                  dateFormat="dd-mm-yy"
                  placeholder="dd-mm-yyyy"
                  disabled={submitting || loading}
                  inputClassName="ms-dialog__input"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div className="ms-dialog__field">
              <label className="ms-dialog__label">
                Replanned Due Date <span className="ms-dialog__required">*</span>
              </label>
              <div className="ms-dialog__date-picker">
                <Calendar
                  value={form.replannedDueDate ? new Date(form.replannedDueDate) : null}
                  onChange={(val) => {
                    const newValue = val.value;
                    if (!newValue) {
                      setField('replannedDueDate', '');
                      return;
                    }
                    setField('replannedDueDate', dayjs(newValue).format('YYYY-MM-DD'));
                  }}
                  dateFormat="dd-mm-yy"
                  placeholder="dd-mm-yyyy"
                  disabled={submitting || loading}
                  inputClassName="ms-dialog__input"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div className="ms-dialog__field ms-dialog__field--fullrow ms-dialog__field--remarks">
              <label className="ms-dialog__label">Remarks</label>
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter..."
                value={form.remarks}
                onChange={(ev) => {
                  setField('remarks', ev.target.value);
                }}
              />
            </div>

          </div>
        )}

        {error && <div className="ms-dialog__error">{error}</div>}

        <div className="ms-dialog__footer">
          <button
            type="button"
            className="ms-btn ms-btn--ghost"
            onClick={onHide}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ms-btn ms-btn--primary"
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting
              ? (mode === 'edit' ? 'Updating...' : 'Adding...')
              : (mode === 'edit' ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
};

MilestoneFormDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  rowData: PropTypes.shape({
    milestone_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    milestoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  token: PropTypes.string,
  onSuccess: PropTypes.func,
};

MilestoneFormDialog.defaultProps = {
  rowData: null,
  token: '',
  onSuccess: null,
};

export default MilestoneFormDialog;
